import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import { Typography } from '@mui/material';
import { Box, Stack } from '@mui/system';
import type { StatusesByField } from 'business/types';
import { translateBlock } from 'i18n';
import type {
    CatalogSelection,
    CatalogTreeField,
} from 'modules/datacatalog/types';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { UNCLASSIFIED } from 'shared/constants';

import TableRenderer from './TableRenderer';

type Props = {
    domain: string;
    category: string;
    shouldDisplayUnclassifiedCategory: boolean;
    tableData: Record<string, CatalogTreeField[]>;
    selectedName: string | null;
    onSelect: (selection: CatalogSelection) => void;
    searchQuery?: string;
    displayedStatuses?: StatusesByField;
};

const CategoryRenderer = ({
    domain,
    category,
    shouldDisplayUnclassifiedCategory,
    tableData,
    selectedName,
    onSelect,
    searchQuery = undefined,
    displayedStatuses,
}: Props) => {
    const { t } = useTranslation();
    const unclassifiedCategoryLabel = shouldDisplayUnclassifiedCategory
        ? t('common.others')
        : '';
    const categoryLabel =
        category === UNCLASSIFIED
            ? unclassifiedCategoryLabel
            : translateBlock(`${domain.toLowerCase()}.${category}`);

    const tables = useMemo(
        () =>
            Object.entries(tableData).sort(([a], [b]) =>
                translateBlock(a).localeCompare(translateBlock(b)),
            ),
        [tableData],
    );
    return (
        <Box sx={{ mt: 1 }}>
            {categoryLabel ? (
                <Stack
                    direction="row"
                    spacing={1}
                    sx={{
                        alignItems: 'center',
                        px: 1,
                        py: 0.5,
                    }}
                >
                    <FolderOutlinedIcon color="disabled" />
                    <Typography variant="body2" color="textSecondary">
                        {categoryLabel}
                    </Typography>
                </Stack>
            ) : null}
            {tables.map(([tableName, fields]) => (
                <TableRenderer
                    key={tableName}
                    tableName={tableName}
                    fields={fields}
                    domain={domain}
                    category={category}
                    isSelected={selectedName === tableName}
                    selectedFieldName={selectedName}
                    onSelect={onSelect}
                    searchQuery={searchQuery}
                    displayedStatuses={displayedStatuses}
                />
            ))}
        </Box>
    );
};

export default CategoryRenderer;
